// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/layout/AppLayout.module.css';
import {useAppLayoutState} from '@app/features/app/components/layout/app_layout/AppLayoutHooks';
import Authentication from '@app/features/auth/state/Authentication';
import Channels from '@app/features/channel/state/Channels';
import {CompanionPanel} from '@app/features/companion/components/CompanionPanel';
import {CompanionRuntime} from '@app/features/companion/components/CompanionRuntime';
import CompanionSessionStore from '@app/features/companion/state/CompanionSessionStore';
import GatewaySessions from '@app/features/gateway/transport/GatewaySessionPool';
import {RecoveryKitReminderGate} from '@app/features/user/components/RecoveryKitReminderGate';
import {MediaDeviceStartupPreloadManager} from '@app/features/voice/components/MediaDeviceStartupPreloadManager';
import {NewDeviceMonitoringManager} from '@app/features/voice/components/NewDeviceMonitoringManager';
import {VoiceReconnectionManager} from '@app/features/voice/components/VoiceReconnectionManager';
import MediaEngine, {useMediaEngineVersion} from '@app/features/voice/engine/MediaEngineFacade';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';

export const AppLayout = observer(({children}: {children: React.ReactNode}) => {
	useMediaEngineVersion();
	const isAuthenticated = Authentication.isAuthenticated;
	const companionOpen = isAuthenticated && CompanionSessionStore.isPanelOpen;
	const connectedChannelId = MediaEngine.channelId;
	const socket = GatewaySessions.foregroundConnection.socket;
	const appState = useAppLayoutState();
	return (
		<>
			{isAuthenticated && <CompanionRuntime data-flx="app.app-layout.companion-runtime" />}
			{isAuthenticated && socket && <VoiceReconnectionManager data-flx="app.app-layout.voice-reconnection-manager" />}
			{isAuthenticated && (
				<MediaDeviceStartupPreloadManager data-flx="app.app-layout.media-device-startup-preload-manager" />
			)}
			{isAuthenticated && <NewDeviceMonitoringManager data-flx="app.app-layout.new-device-monitoring-manager" />}
			{isAuthenticated && <RecoveryKitReminderGate data-flx="app.app-layout.recovery-kit-reminder-gate" />}
			<div
				className={clsx(
					styles.appLayout,
					appState.isStandalone && styles.appLayoutStandalone,
					companionOpen && styles.withCompanion,
				)}
				data-flx="app.app-layout.app-layout"
			>
				{children}
				{companionOpen && (
					<CompanionPanel
						connectedChannelId={connectedChannelId}
						connectedChannelName={connectedChannelId ? Channels.getChannel(connectedChannelId)?.name : undefined}
						data-flx="app.app-layout.companion-panel"
					/>
				)}
			</div>
		</>
	);
});
